/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-continue */
/* eslint-disable class-methods-use-this */
import { NormalizedAbstractVisitor } from './NormalizedAbstractVisitor'
import { VisitorContext, NodesById, State, NormalizedBuilderNode } from '../types'
import { ReportGrammarRules } from '../../../../../../../redux/config/customReport/types'

/**
 * Visitor class that finds all valid paths to referenceable nodes
 * while detecting and preventing circular references
 */
export class ReferenceablePathsVisitor extends NormalizedAbstractVisitor<string[][]> {
  private rootNodeId: string

  constructor(rules: ReportGrammarRules, nodesById: NodesById, rootNodeId: string) {
    super(rules, nodesById, true)
    this.rootNodeId = rootNodeId
  }

  /**
   * Find all valid reference paths from the current node
   * @param referenceNodeId The node that wants to create references
   * @returns Array of valid node paths that can be referenced
   */
  public findReferenceablePaths(referenceNodeId: string): NormalizedBuilderNode[][] {
    // Find all valid paths in the node graph
    const validPaths = this.findAllValidPaths(referenceNodeId)

    // Convert path IDs to actual nodes
    return validPaths.map(path => path.map(id => this.nodesById[id]))
  }

  /**
   * Find all valid paths that won't create circular references
   */
  private findAllValidPaths(referenceNodeId: string): string[][] {
    const result: string[][] = []

    // Build adjacency list representation of the graph
    const graph = this.buildNodeGraph()

    // Find all referenceable nodes
    const referenceableNodes = this.findReferenceableNodes(referenceNodeId, graph)

    // For each referenceable node, find a path to it from root
    for (const nodeId of referenceableNodes) {
      const path = this.findPath(this.rootNodeId, nodeId, graph)
      if (path.length > 0) {
        result.push(path)
      }
    }

    return result
  }

  /**
   * Build an adjacency list representation of the node graph
   * @returns A map of node IDs to their children and parents
   */
  private buildNodeGraph(): Map<string, { children: string[]; parent: string | null }> {
    const graph = new Map<string, { children: string[]; parent: string | null }>()

    // Initialize all nodes in the graph
    Object.keys(this.nodesById).forEach(id => {
      graph.set(id, { children: [], parent: null })
    })

    // Populate children and parents
    Object.entries(this.nodesById).forEach(([id, node]) => {
      if (node.childIds && node.childIds.length > 0) {
        // Set children
        graph.get(id)!.children = [...node.childIds]

        // Set parent for each child
        node.childIds.forEach(childId => {
          const childNode = graph.get(childId)
          if (childNode) {
            childNode.parent = id
          }
        })
      }
    })

    return graph
  }

  /**
   * Find all nodes that can be referenced from the given node
   * without creating circular references
   */
  private findReferenceableNodes(
    referenceNodeId: string,
    graph: Map<string, { children: string[]; parent: string | null }>
  ): Set<string> {
    const referenceableNodes = new Set<string>()

    // Get all ancestors of the reference node (can't reference these to avoid cycles)
    const ancestors = this.getAncestors(referenceNodeId, graph)

    // Check each node in the graph
    for (const [nodeId] of graph.entries()) {
      // Skip the reference node itself
      if (nodeId === referenceNodeId) continue

      // Skip ancestors of the reference node (would create a cycle)
      if (ancestors.has(nodeId)) continue

      // Get the node from our nodes dictionary
      const node = this.nodesById[nodeId]
      if (!node) continue

      // Check if node is referenceable based on type
      const context: VisitorContext = { state: State.Root }

      // Special handling for period groups - always make them referenceable
      if (node.type === 'periodGroup' || this.isReferenceable(node, context)) {
        // For reference nodes, need to check if they would create indirect cycles
        if (node.type === 'reference' && typeof node.value === 'string') {
          if (!this.wouldCreateCycle(node.value, referenceNodeId, graph, new Set())) {
            referenceableNodes.add(nodeId)
          }
        } else {
          // Regular referenceable node
          referenceableNodes.add(nodeId)
        }
      }
    }

    return referenceableNodes
  }

  /**
   * Check if referencing targetId from referenceNodeId would create a cycle
   * through chained references
   */
  private wouldCreateCycle(
    targetId: string,
    referenceNodeId: string,
    graph: Map<string, { children: string[]; parent: string | null }>,
    visited: Set<string>
  ): boolean {
    // If we've seen this node before, we have a cycle
    if (visited.has(targetId)) return true

    // If this node is our reference node, we would create a cycle
    if (targetId === referenceNodeId) return true

    // Mark as visited
    visited.add(targetId)

    const node = this.nodesById[targetId]

    // If not a reference node, no cycle possible through this path
    if (!node || node.type !== 'reference' || typeof node.value !== 'string') {
      return false
    }

    // Recursively check the reference target
    return this.wouldCreateCycle(node.value, referenceNodeId, graph, visited)
  }

  /**
   * Get all ancestors of a node
   */
  private getAncestors(nodeId: string, graph: Map<string, { children: string[]; parent: string | null }>): Set<string> {
    const ancestors = new Set<string>()

    let current = graph.get(nodeId)
    while (current && current.parent) {
      ancestors.add(current.parent)
      current = graph.get(current.parent)
    }

    return ancestors
  }

  /**
   * Find a path from start to end in the graph
   * Uses BFS to find the shortest path
   */
  private findPath(
    startId: string,
    endId: string,
    graph: Map<string, { children: string[]; parent: string | null }>
  ): string[] {
    // If start and end are the same, return just that node
    if (startId === endId) return [startId]

    // Queue for BFS
    const queue: { id: string; path: string[] }[] = [{ id: startId, path: [startId] }]
    const visited = new Set<string>([startId])

    while (queue.length > 0) {
      const { id, path } = queue.shift()!
      const node = graph.get(id)

      if (!node) continue

      // Check each neighbor (child)
      for (const childId of node.children) {
        if (childId === endId) {
          // Found the target node!
          return [...path, childId]
        }

        if (!visited.has(childId)) {
          visited.add(childId)
          queue.push({ id: childId, path: [...path, childId] })
        }
      }
    }

    return [] // No path found
  }

  // Required abstract method implementations
  visitRootNode(node: any, context: VisitorContext): string[][] {
    return []
  }

  visitCompanyNode(node: any, context: VisitorContext): string[][] {
    return []
  }

  visitDimensionNode(node: any, context: VisitorContext): string[][] {
    return []
  }

  visitPeriodGroupNode(node: any, context: VisitorContext): string[][] {
    return []
  }

  visitFunctionNode(node: any, context: VisitorContext): string[][] {
    return []
  }

  visitReferenceNode(node: any, context: VisitorContext): string[][] {
    return []
  }

  visitKeyFigureNode(node: any, context: VisitorContext): string[][] {
    return []
  }

  visitFormulaNode(node: any, context: VisitorContext): string[][] {
    return []
  }
}
